import classNames from "classnames";
import { motion } from "motion/react";
import React from "react";
import styles from "../../styles/Form.module.css";
import PulseLoader from "../Loader/PulseLoader";

interface ButtonProps {
  addon?: string;
  ariaExpanded?: boolean;
  ariaLabel?: string;
  asLink?: boolean;
  children?: React.ReactNode;
  className?: string;
  color?: "primary" | "red" | "transparent" | "primaryText" | "ignore" | "primaryColor";
  disabled?: boolean;
  inverse?: boolean;
  loading?: boolean;
  noPadding?: boolean;
  onClick?: () => void;
  plain?: boolean;
  pulseColor?: string;
  rounded?: boolean;
  small?: boolean;
  style?: React.CSSProperties;
  textColorClass?: string;
  title?: string;
  type?: "submit" | "button" | "reset" | undefined;
  widthClass?: string;
}

function Button({
  addon,
  ariaExpanded,
  ariaLabel,
  asLink = false,
  children,
  className,
  color = "primary",
  disabled = false,
  inverse = false,
  loading = false,
  noPadding = false,
  onClick,
  plain = false,
  pulseColor = "",
  rounded = true,
  small = false,
  style = {},
  textColorClass,
  title,
  type = "submit",
  widthClass = "w-auto",
}: ButtonProps) {
  const content = loading ? (
    <div className="relative">
      <div className="invisible">{children}</div>
      <PulseLoader
        className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
        color={pulseColor}
        size={1.5}
      />
    </div>
  ) : (
    children
  );

  if (!asLink && !plain) {
    style.backgroundImage =
      "linear-gradient(to top right, rgba(255,255,255,0), rgba(255,255,255,0.3)), linear-gradient(to top left, rgba(255,255,255,0), rgba(255,255,255,0.15)), linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.05))";
  }

  return (
    <motion.button
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel ?? (typeof children === "string" ? children : undefined)}
      disabled={disabled}
      onClick={onClick}
      title={title}
      type={type}
      whileTap={disabled ? undefined : { scale: 0.95 }}
      className={plain
        ? classNames("flex items-center", { "rounded-md": rounded }, className)
        : classNames(
          asLink ? styles.link : styles.button,
          className,
          inverse ? styles.inverse : styles.normal,
          inverse ? "" : styles[color || ""],
          {
            "rounded-md": rounded,
            "py-2 px-8": !addon && !asLink && !noPadding,
            [`shadow-md ${widthClass ? widthClass : "w-full"}`]: !asLink,
          },
          `sm:${widthClass}`,
          "flex border border-transparent sm:order-last",
        )}
      style={style}
    >
      {plain ? content : (
        <div
          className={classNames(
            { "py-2 px-8": addon, "text-sm": small },
            textColorClass,
            "font-medium flex-1",
          )}
        >
          {content}
        </div>
      )}
      {addon && (
        <div className="h-full px-4 py-2 font-light font-number bg-white/25 rounded-r-md">
          {addon}
        </div>
      )}
    </motion.button>
  );
}

export default Button;
